/**
 * The week's plant outlook for the signed-in gardener's garden, shared by
 * every card and page that reads it: each plant's graded week and verdicts,
 * the week's weather against its normals, and the weather now. The grading
 * thresholds it was judged by are the server's to use: the app says nothing
 * from them (audit batch 4: the card's paragraphs and the About sheet are
 * the server's).
 *
 * One request, GET /plants/outlook (api/outlook), cached under
 * [PLANT_OUTLOOK_QUERY_KEY, zip, tempUnit] ('plant-outlook') for
 * OUTLOOK_STALE_TIME_MS (30 minutes); any change to plants, beds or stages
 * invalidates 'plant-outlook'. The plants are the garden being tended's
 * (X-Grow-Garden). The zip here is the garden's, as the server says it (the
 * garden place, useGardenPostcode: the owner's in a shared garden, never
 * the member's own; audit A29): it is sent in the person's own garden, and
 * in a shared garden it is left off (api/outlook, gardenMembers.gardenZip),
 * so the week is read for the owner's postcode. Whether to ask at all is the
 * server's answer too (missingZip: the garden cannot be planned).
 *
 * A card whose plant has a brief week but no row here calls `refetch`, which
 * reads the outlook again once per state for all cards (B28; never cancelling
 * a read in flight, so the rate limit of B24 is not hit by a burst).
 *
 * Who uses it: every plant card, row and tile, the garden's conditions bar,
 * Today, the Weather page and useWeatherNow. Today and the Weather page
 * read `error` too, to say the standard sentence when the outlook failed on
 * our side (D31, lib/serverFailure).
 *
 * @module usePlantOutlook
 */

import { retryRule } from '@/config/queryRetry';
import { useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { useUserConfig } from '@/shared/context/userConfigStore';
import { OUTLOOK_STALE_TIME_MS, PLANT_OUTLOOK_QUERY_KEY, QUERY_RETRY_COUNT } from '@/config/tunables/shared';
import { createRefetchGate } from '@/shared/lib/refetchGate';
import { getPlantOutlook, type PlantOutlook, type OutlookSummary, type OutlookConditions, type OutlookCurrent, type OutlookObserved } from '@/shared/api/outlook';

/** One quiet refetch per outlook state for every card (B28). */
const refetchGate = createRefetchGate();

/** What the hook hands back: lookups, the garden-wide blocks, and the request's state. */
export interface UsePlantOutlookResult {
  /** Outlook for one plant, or null when it has none. */
  forPlant: (plantObjId: number) => PlantOutlook | null;
  /**
   * Every plant's outlook, for anything speaking about the garden as a whole.
   *
   * forPlant answers one card's question. A bar above the grid asks a
   * different one — how many plants are in trouble, and with what — and that
   * cannot be built from a lookup without knowing the keys.
   */
  plants: PlantOutlook[];
  /** The garden-wide framing: the week against normal, its icons and Trellis's own account. */
  summary: OutlookSummary | null;
  /** The week's weather and the normals behind the advice. */
  conditions: OutlookConditions | null;
  /** Conditions right now, from the same forecast reading. */
  current: OutlookCurrent | null;
  /** The weather that fell at the garden before today (kept past weather); null when none is kept. */
  observed: OutlookObserved | null;
  /** True on the first fetch, with nothing cached yet. */
  isLoading: boolean;
  /**
   * True while a request is in flight, including a background refetch.
   *
   * A plant added a moment ago has no entry yet, and the difference between
   * "no advice exists" and "the advice is on its way" is the difference
   * between showing a fallback and waiting a second.
   */
  isFetching: boolean;
  /**
   * True when the outlook could not be fetched.
   *
   * Distinct from "nothing is urgent". A caller that treats a failure as an
   * all-clear tells the gardener their garden is fine on the strength of a
   * request that never arrived.
   */
  isError: boolean;
  /** The failure itself, when there is one (null otherwise), so a page can tell a failure on our side from the rest. */
  error: unknown;
  /**
   * True once there is an answer to read, stale or fresh.
   *
   * A refetch that fails leaves the last good outlook in the cache, and every
   * card goes on drawing from it. Calling that "no answer" made the chip say
   * it did not know while the cards beside it showed red dots derived from the
   * very same data. One source, one story: if the cards can read it, so can
   * the chip. Only a failure with nothing cached behind it is no answer.
   */
  hasAnswer: boolean;
  /** True when the server says the garden cannot be planned (no usable postcode), so no outlook can be fetched. */
  missingZip: boolean;
  /** When the outlook now held arrived (ms since the epoch); 0 before any. */
  updatedAt: number;
  /**
   * How many times an outlook has arrived under this postcode and unit: 1
   * after the first read, more once a write (a plant added, a bed changed)
   * or its staleness has had it read again. The Today brief, built on the
   * outlook, is read again when this passes 1 (useTodayBrief).
   */
  updates: number;
  /** Reads the outlook again, quietly (B28: a card whose plant has no row yet); a failed read keeps the last outlook. */
  refetch: () => void;
}

/**
 * The week's outlook for the user's garden, keyed by plant.
 *
 * Every plant card calls this. React Query deduplicates on the shared key, so
 * the garden is fetched once per postal code rather than once per card — the
 * backend already returns all plants in a single response.
 *
 * @returns Lookups, the garden-wide blocks, and the request's state.
 */
export function usePlantOutlook(): UsePlantOutlookResult {
  const { userConfig } = useUserConfig();
  // The garden's postcode, and whether it can be planned, as the server says (the owner's in a shared garden).
  const place = useGardenPostcode();
  const zip = place.usable ? place.zip : '';
  const tempUnit = userConfig.preferences.temperatureUnit === 'C' ? 'c' : 'f';

  const queryClient = useQueryClient();
  const queryKey = [PLANT_OUTLOOK_QUERY_KEY, zip, tempUnit];
  const { data, dataUpdatedAt, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: () => getPlantOutlook(zip, tempUnit),
    enabled: zip.length > 0,
    staleTime: OUTLOOK_STALE_TIME_MS,
    // The outlook is advisory. A failure should leave the card showing its own
    // temperature reading rather than an error, so this never retries hard.
    retry: retryRule(QUERY_RETRY_COUNT),
  });

  // Both memoised because every plant card calls this hook. A fresh Map and a
  // fresh closure on each render gave forPlant a new identity every time, so
  // any useMemo or useEffect depending on it re-ran on every render of every
  // card — the weather page rebuilt its whole impact list that way.
  const byPlant = useMemo(
    () => new Map((data?.plants ?? []).map((p) => [p.plantObjId, p])),
    [data]
  );
  /**
   * One plant's outlook.
   *
   * @param plantObjId - The plant.
   * @returns Its outlook, or null when it has none.
   */
  const forPlant = useCallback(
    (plantObjId: number) => byPlant.get(plantObjId) ?? null,
    [byPlant]
  );

  /**
   * Reads the outlook again, once per outlook state however many cards ask
   * (a shared gate, so a remounted fold asks no more), without cancelling a
   * read in flight and without surfacing a failure.
   */
  const refetchQuietly = useCallback(() => {
    const key = `${zip}|${tempUnit}`;
    if (!refetchGate.claim(key, dataUpdatedAt)) return;
    void refetch({ cancelRefetch: false }).then((r) => refetchGate.settle(key, r.dataUpdatedAt), () => undefined);
  }, [refetch, zip, tempUnit, dataUpdatedAt]);

  return {
    forPlant,
    plants: data?.plants ?? [],
    summary: data?.summary ?? null,
    conditions: data?.conditions ?? null,
    current: data?.current ?? null,
    observed: data?.observed ?? null,
    isLoading: zip.length > 0 && isLoading,
    isFetching: zip.length > 0 && isFetching,
    isError: zip.length > 0 && isError,
    error: zip.length > 0 ? error : null,
    hasAnswer: zip.length > 0 && data !== undefined,
    // Only once the server has said: until then nothing is missing, only on its way.
    missingZip: place.known && !place.usable,
    updatedAt: zip.length > 0 ? dataUpdatedAt : 0,
    // Read from the cache on each render; every arrival re-renders this hook's callers.
    updates: zip.length > 0 ? queryClient.getQueryState(queryKey)?.dataUpdateCount ?? 0 : 0,
    refetch: refetchQuietly,
  };
}
